import React, { useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';

import ApiService from '../services/ApiService';
import { useAuth } from '../context/auth';
import { exportIngredientRequirementsPdf } from '../utils/pdf/exportIngredientRequirementsPdf';
import { loadImageAsDataUrl } from '../utils/pdf/image';
import { showToast } from '../utils/ui/toast';

const api = new ApiService();

const MIN_INPUT_VALUE = 0;
const DEFAULT_TOAST_LIFE = 3000;

const formatKg = (value) =>
	new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value ?? 0);

const MachineIngredientRequirements = (props) => {
	const toastRef = useRef(null);
	const { authTokens } = useAuth();

	const machineId = props.location?.state?.machineId;
	const machineName = props.location?.state?.customerName || props.location?.state?.name || machineId;

	const [recipesPerDay, setRecipesPerDay] = useState(MIN_INPUT_VALUE);
	const [daysToCover, setDaysToCover] = useState(MIN_INPUT_VALUE);

	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [customerName, setCustomerName] = useState('');

	const totals = useMemo(
		() =>
			data.reduce(
				(acc, cur) => {
					acc.kg += cur.killograms ?? 0;
					acc.boxes += cur.boxes ?? 0;
					acc.palletTotal += cur.totalBoxWeight ?? 0;
					return acc;
				},
				{ kg: 0, boxes: 0,  palletTotal: 0},
			),
		[data],
	);

	const handleCalculate = async () => {
		if (!machineId) {
			showToast(toastRef, 'error', 'Error', 'Missing machine ID. Please refresh the page.', 3500);
			return;
		}
		if (recipesPerDay < MIN_INPUT_VALUE || daysToCover < MIN_INPUT_VALUE) {
			showToast(toastRef, 'warn', 'Invalid Input', 'Values cannot be negative.', DEFAULT_TOAST_LIFE);
			return;
		}
		if (recipesPerDay === 0 || daysToCover === 0) {
			showToast(toastRef, 'info', 'Missing Values', 'Please enter recipes per day and days to cover.', 2500);
			return;
		}

		const headers = {
			'X-Firebase-Auth-Client': authTokens?.token,
		};

		setLoading(true);
		try {
			const response = await api.getIngredientForecast(machineId, recipesPerDay, daysToCover, headers);
			const payload = Array.isArray(response?.data) ? response.data : [];

			setData(payload);
			if (payload.length > 0) {
				const nonZeroCount = payload.filter((item) => (item.killograms ?? 0) > 0 || (item.boxes ?? 0) > 0).length;
				showToast(
					toastRef,
					'success',
					'Calculated',
					`Found requirements for ${nonZeroCount} active ingredients.`,
					2500,
				);
			} else {
				showToast(toastRef, 'info', 'No Data', 'No forecast returned from the server.', 2500);
			}
		} catch (e) {
			console.error('Failed to fetch ingredient forecast', e);
			const errorDetail =
				e?.response?.status === 404
					? 'Machine not found or endpoint unavailable.'
					: e?.response?.status === 403
					? 'You do not have permission to access this data.'
					: 'Failed to load ingredient requirements. Please try again.';
			showToast(toastRef, 'error', 'Error', errorDetail, 4000);
			setData([]);
		} finally {
			setLoading(false);
		}
	};

	const handleExportPdf = async () => {
		if (!data.length) {
			showToast(toastRef, 'warn', 'No Data', 'Please calculate requirements first.', 2500);
			return;
		}
		setIsExporting(true);
		try {
			// Try to load a default logo from public path if available
			const defaultLogoCandidates = ['/branding/alberts-logo.png', '/alberts-logo.png', '/logo.png'];
			let logoDataUrl = null;
			for (const candidate of defaultLogoCandidates) {
				// eslint-disable-next-line no-await-in-loop
				const maybe = await loadImageAsDataUrl(candidate);
				if (maybe) {
					logoDataUrl = maybe;
					break;
				}
			}

			exportIngredientRequirementsPdf(
				data,
				{ machineId, recipesPerDay, daysToCover },
				{
					machineName,
					customerName: customerName?.trim() || undefined,
					logoDataUrl,
					companyName: 'Alberts NV',
					companyPhone: '+32 3 361 76 22',
					companyEmail: 'team@alberts.be',
					companyAddress: 'Bijkhoevelaan 32c, 2110 Wijnegem, Belgium',
					companyVat: 'BE 0635.874.689',
					disclaimer: 'Indicative forecast for internal client communication only.',
				},
			);
			showToast(toastRef, 'success', 'Exported', 'PDF has been downloaded successfully.', 2000);
		} catch (err) {
			console.error('PDF export failed', err);
			showToast(toastRef, 'error', 'Export Failed', 'Could not generate PDF. Please try again.', 3500);
		} finally {
			setIsExporting(false);
		}
	};

	const isZeroRow = (row) => (row.killograms ?? 0) === 0 && (row.boxes ?? 0) === 0;

	const renderCell = (content, row) => <span style={{ opacity: isZeroRow(row) ? 0.5 : 1 }}>{content}</span>;

	const header = (
		<div className="flex align-items-center justify-content-between w-full">
			<div className="flex align-items-center gap-3 flex-wrap">
				<span className="text-900 font-semibold">Ingredient Forecast</span>
				{data.length > 0 && (
					<span className="text-600 text-sm">({data.filter((item) => !isZeroRow(item)).length} active)</span>
				)}
			</div>
			<div className="flex align-items-center gap-2">
				<Button
					label="Export PDF"
					icon={isExporting ? 'pi pi-spin pi-spinner' : 'pi pi-file-pdf'}
					severity="warning"
					outlined
					onClick={handleExportPdf}
					aria-label="Export ingredient requirements to PDF"
					disabled={!data.length || isExporting}
					loading={isExporting}
					style={{
						opacity: !data.length || isExporting ? 0.6 : 1,
						cursor: !data.length || isExporting ? 'not-allowed' : 'pointer',
					}}
				/>
			</div>
		</div>
	);

	const footer = (
		<div className="flex justify-content-end w-full">
			<div className="flex gap-4 text-900">
				<span className="font-medium">Total ingredients required: {formatKg(totals.kg)} kg</span>
				<span className="font-medium">Total PALLET: {formatKg(totals.palletTotal)} kg</span>
				<span className="font-medium">Total Boxes: {totals.boxes}</span>
			</div>
		</div>
	);

	const emptyMessage = (
		<div className="flex flex-column align-items-center justify-content-center py-6 text-center">
			<i className="pi pi-calculator text-400 mb-3" style={{ fontSize: '2.5rem' }}></i>
			<p className="text-700 font-semibold mb-2">No Data Yet</p>
			<p className="text-600 text-sm">Enter values above and click Calculate to see ingredient requirements.</p>
		</div>
	);

	return (
		<Card className="mt-3 shadow-1 border-round-xl">
			<Toast ref={toastRef} />
			<div className="flex flex-column gap-3">
				<div className="surface-50 border-round-lg p-3">
					<div className="flex flex-wrap align-items-end gap-3">
						<div className="flex flex-column">
							<label htmlFor="recipesPerDay" className="text-600 text-sm mb-1">
								Recipes per day
							</label>
							<InputNumber
								id="recipesPerDay"
								inputId="recipesPerDay"
								value={recipesPerDay}
								onValueChange={(e) => setRecipesPerDay(e.value ?? MIN_INPUT_VALUE)}
								min={MIN_INPUT_VALUE}
								showButtons
								buttonLayout="horizontal"
								decrementButtonClassName="p-button-sm p-button-secondary"
								incrementButtonClassName="p-button-sm p-button-secondary"
								aria-label="Number of recipes per day"
								disabled={loading}
							/>
						</div>

						<div className="flex flex-column">
							<label htmlFor="customerName" className="text-600 text-sm mb-1">
								Customer name (optional)
							</label>
							<InputText
								id="customerName"
								value={customerName}
								onChange={(e) => setCustomerName(e.target.value)}
								placeholder="e.g. ACME Corp"
								aria-label="Optional customer name for PDF header"
								disabled={loading}
							/>
						</div>

						<div className="flex flex-column">
							<label htmlFor="daysToCover" className="text-600 text-sm mb-1">
								Days to cover
							</label>
							<InputNumber
								id="daysToCover"
								inputId="daysToCover"
								value={daysToCover}
								onValueChange={(e) => setDaysToCover(e.value ?? MIN_INPUT_VALUE)}
								min={MIN_INPUT_VALUE}
								showButtons
								buttonLayout="horizontal"
								decrementButtonClassName="p-button-sm p-button-secondary"
								incrementButtonClassName="p-button-sm p-button-secondary"
								aria-label="Number of days to cover"
								disabled={loading}
							/>
						</div>

						<div className="flex gap-2">
							<Button
								label="Calculate"
								icon="pi pi-calculator"
								severity="primary"
								onClick={handleCalculate}
								aria-label="Calculate ingredient forecast"
								loading={loading}
								disabled={loading}
							/>
						</div>
					</div>
				</div>

				{/* Calculation info */}
				<div
					className="border-1 border-round-lg p-3"
					style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderColor: '#bfdbfe' }}
				>
					<div className="flex align-items-start gap-3">
						<i className="pi pi-info-circle" style={{ color: '#3b82f6', fontSize: '1.1rem' }} aria-hidden="true"></i>
						<div className="flex flex-column gap-1">
							<span className="text-900 font-medium">How this is calculated</span>
							<span className="text-700 text-sm">
								We calculate required quantities across all ingredients configured for this machine and only consider
								recipes that are currently selected on the machine.
							</span>
							<span className="text-600 text-sm">
								If an ingredient is in the machine but no selected recipe uses it, it is simulated as 0 kg and 0 boxes
								and will appear dimmed in the table.
							</span>
						</div>
					</div>
				</div>

				{loading ? (
					<div className="flex align-items-center justify-content-center py-6">
						<ProgressSpinner />
					</div>
				) : (
					<DataTable
						value={data}
						header={header}
						footer={data.length > 0 ? footer : null}
						emptyMessage={emptyMessage}
						tableStyle={{ minWidth: '56rem' }}
						responsiveLayout="scroll"
					>
						<Column
							field="ingredientName"
							header="Ingredient"
							body={(row) => (
								<div style={{ opacity: isZeroRow(row) ? 0.5 : 1 }}>
									<div className="text-900 font-medium">{row.ingredientName}</div>
									<div className="text-500 text-xs">{row.ingredientId}</div>
								</div>
							)}
							style={{ minWidth: '280px' }}
						/>
						<Column
							field="killograms"
							header="Kg"
							body={(row) => renderCell(`${formatKg(row.killograms)} kg`, row)}
							style={{ width: '140px' }}
							/>
						<Column
							field="boxweight"
							header="Box weight"
							body={(row) => renderCell(`${formatKg(row.totalBoxWeight/row.boxes)} kg`, row)}
							style={{ width: '140px' }}
						/>
						<Column
							field="boxes"
							header="Boxes"
							body={(row) => renderCell(row.boxes, row)}
							style={{ width: '140px' }}
						/>
					</DataTable>
				)}
			</div>
		</Card>
	);
};

export default MachineIngredientRequirements;
